import { defineStore } from 'pinia';
import { ref } from 'vue';
import type {
  FileStreamResult,
  Invoice,
  InvoiceCombinedDownloadRequest,
  InvoiceCreateRequest,
  SimulatedInvoice,
} from '@/api/client/models/index.ts';
import { useApiClient } from '@/hooks/api-hook.ts';
import { createFileDownloadResponseHandlerOption, downloadFile } from '@/util/file-download.ts';

export const useFinancesStore = defineStore('finances', () => {
  const apiClient = useApiClient();

  const list = ref<Invoice[]>([]);
  const abstract = ref<null>(null);
  const added = ref<Invoice | null>(null);
  const loading = ref<boolean>(true);

  async function simulate(data: InvoiceCreateRequest): Promise<SimulatedInvoice | undefined> {
    return await apiClient.invoice.simulate.post(data);
  }

  async function generate(data: InvoiceCreateRequest): Promise<Invoice | undefined> {
    const response = await apiClient.invoice.post(data);
    added.value = response ?? null;
    return response;
  }

  async function combinedDownload({ data }: { data?: InvoiceCombinedDownloadRequest } = {}): Promise<FileStreamResult | undefined> {
    const response = await apiClient.invoice.downloadCombined.post(data ?? {}, {
      options: [createFileDownloadResponseHandlerOption()],
    }) as unknown as FileStreamResult | undefined;

    return downloadFile(response, 'Invoices.xlsx');
  }

  async function getFile({ id, isPostData = false }: { id?: number; isPostData?: boolean } = {}): Promise<FileStreamResult | undefined> {
    if (id === undefined) {
      return undefined;
    }

    const response = await apiClient.invoice.download.byId(id).get({
      options: [createFileDownloadResponseHandlerOption()],
      queryParameters: {
        isPostData,
      },
    }) as unknown as FileStreamResult | undefined;

    return downloadFile(response, `Invoice-${id}.xlsx`);
  }

  async function get({ showOnlyActive }: { showOnlyActive?: boolean } = {}): Promise<Invoice[]> {
    loading.value = true;

    try {
      const response = await apiClient.invoice.get({
        queryParameters: {
          isActive: showOnlyActive,
        },
      });

      list.value = response ?? [];
      return list.value;
    } finally {
      loading.value = false;
    }
  }

  return {
    list,
    abstract,
    added,
    loading,
    simulate,
    generate,
    combinedDownload,
    getFile,
    get,
  };
});
